// Passkey (WebAuthn) login ceremony — loaded only on the login page.
// Calls /auth/webauthn/login/begin, invokes navigator.credentials.get(),
// then sends the assertion to /auth/webauthn/login/finish.

function b64urlToBuffer(b64: string): ArrayBuffer {
  const padded = b64.replace(/-/g, "+").replace(/_/g, "/").padEnd(
    b64.length + ((4 - (b64.length % 4)) % 4), "="
  );
  const bin = atob(padded);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0)).buffer;
}

function bufferToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

async function passkeyLogin(): Promise<void> {
  // 1. Get options from server
  const beginResp = await fetch("/auth/webauthn/login/begin", { method: "POST" });
  if (!beginResp.ok) {
    alert("No passkeys registered or server error.");
    return;
  }
  const opts = await beginResp.json() as PublicKeyCredentialRequestOptionsJSON;

  // 2. Decode challenge and allowCredentials
  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: b64urlToBuffer(opts.challenge),
    rpId: opts.rpId,
    timeout: opts.timeout ?? 60000,
    userVerification: (opts.userVerification as UserVerificationRequirement) ?? "preferred",
    allowCredentials: (opts.allowCredentials ?? []).map((c) => ({
      id: b64urlToBuffer(c.id),
      type: "public-key" as PublicKeyCredentialType,
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };

  // 3. Invoke the platform authenticator
  let assertion: PublicKeyCredential;
  try {
    assertion = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential;
  } catch (err) {
    console.warn("Passkey cancelled or not available", err);
    return;
  }

  const response = assertion.response as AuthenticatorAssertionResponse;

  // 4. Send to server
  const body = {
    id: assertion.id,
    rawId: bufferToB64url(assertion.rawId),
    type: assertion.type,
    response: {
      authenticatorData: bufferToB64url(response.authenticatorData),
      clientDataJSON: bufferToB64url(response.clientDataJSON),
      signature: bufferToB64url(response.signature),
      userHandle: response.userHandle ? bufferToB64url(response.userHandle) : null,
    },
  };

  const finishResp = await fetch("/auth/webauthn/login/finish", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (finishResp.ok) {
    window.location.href = "/";
  } else {
    const err = await finishResp.json().catch(() => ({}));
    alert("Passkey sign-in failed: " + (err.error ?? "unknown error"));
  }
}

// Wire up the passkey button on DOMContentLoaded
document.addEventListener("DOMContentLoaded", () => {
  const btn = document.getElementById("passkey-btn");
  if (btn) {
    if (!window.PublicKeyCredential) {
      btn.remove();
      return;
    }
    btn.addEventListener("click", () => void passkeyLogin());
  }
});

// Minimal type stubs for options JSON shapes (not in lib.dom by default)
interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  rpId?: string;
  timeout?: number;
  userVerification?: string;
  allowCredentials?: Array<{ id: string; type: string; transports?: string[] }>;
}
